/* ==========================================================================
   KITSTERS - MAGIC HOUR CART GUARD (sitewide)
   --------------------------------------------------------------------------
   Owner 2026-08-19: "still issue is that in normal cart also, magic hour
   product is added. So fix that issue."

   WHY MAGIC KITS EVER TOUCH THE REAL CART AT ALL: Shopify's checkout can
   only sell what is in the cart, so the Magic Hour hand-off (the MH PDP's
   express path and the magic cart's checkout - the only two writers) has to
   clear the cart, add the kits, and navigate to /checkout. Complete the
   payment and Shopify empties the cart itself. ABANDON it - back button,
   closed tab - and the kits are still sitting in the real cart, so the
   Kalles drawer, the header badge and /cart all show them. That is the
   report above.

   THE FIX: both hand-offs stamp localStorage `kt_magic_handoff` immediately
   before navigating. This guard runs on EVERY page (theme.liquid), and when
   - and only when - that stamp exists, it reads /cart.js and removes every
   line whose vendor is the hidden Magic Hour vendor, then clears the
   hand-off's attributes + note. No stamp -> not one network request, so the
   other ~130 pages pay nothing for it (the stamp can only exist after a
   hand-off, and only ever briefly).

   Why gate on a stamp instead of sweeping unconditionally: an unconditional
   sweep is a /cart.js GET on every page view sitewide forever, for a state
   that can only exist after a Magic Hour hand-off. The stamp makes the
   common case free and the interesting case exact (§10.90's "group the
   bytes before optimising" instinct, applied to requests).

   The vendor comes from the kt-hidden-vendor META that layout/theme.liquid
   already publishes on every page (settings.kt_hidden_vendor - the same
   single source of truth the catalogue-hiding sweep uses), with the literal
   as fallback so a blanked setting cannot turn the guard off silently.

   Line removal uses ONE /cart/update.js call with the lines' KEYS (quantity
   0). Keys, not variant ids, and as strings - documented Ajax Cart API
   behaviour (re-checked 2026-08-19): a cart can hold several lines of one
   variant when properties differ, and the key targets exactly the intended
   line.

   Edge worth stating: if the shopper abandoned checkout and then added
   RETAIL items before this guard ran, only the magic lines are removed and
   the retail lines survive - which is why this is never /cart/clear.js
   unless the cart is already empty of everything else (clear.js is used in
   that case because it also resets attributes + note wholesale).
   ========================================================================== */
(function () {
  'use strict';

  var FLAG = 'kt_magic_handoff';

  /* The attribute keys the hand-offs write - cleared here so an abandoned
     Magic Hour checkout can never leak "Company name" / "Order type: Magic
     Hour" attributes onto a later RETAIL order. Keep in step with
     orderAttributes() in kitsters-magic-cart.js / kitsters-magic-hour-
     product.js. */
  /* Must match what THIS tree's hand-offs actually write (kitsters-magic-cart.js
     orderAttributes / kitsters-magic-hour-product.js) - plus anything they used
     to write. 'PO number' is deliberately KEPT after the field was removed on
     2026-08-31: this list only ever CLEARS attributes, so a stale key costs
     nothing, while dropping it would strand the attribute on a cart abandoned
     before the change. */
  var HANDOFF_ATTRS = [
    'Company name', 'PO number', 'Ordered by', 'Designation',
    'Contact email', 'Contact phone', 'Registered address',
    'Deliver to registered address', 'Order type', 'Hours', 'Kits', 'Packs'
  ];

  function readFlag() {
    try { return window.localStorage.getItem(FLAG); } catch (e) { return null; }
  }
  function clearFlag() {
    try { window.localStorage.removeItem(FLAG); } catch (e) { /* no-op */ }
  }

  function hiddenVendor() {
    var m = document.querySelector('meta[name="kt-hidden-vendor"]');
    return (m && m.getAttribute('content')) || 'Magic Hour';
  }

  function announce(cart) {
    /* Tell the real cart's UI what just changed, or the drawer/badge keep
       showing lines that are gone until a reload (§10.71). Dawn's drawer has
       no cart:reload listener (that was Kalles) - the cart bridge owns the
       re-render on this tree. */
    if (window.KitstersCart && window.KitstersCart.reload) {
      window.KitstersCart.reload();
    }
    document.dispatchEvent(new CustomEvent('cart:count:update', {
      detail: { item_count: cart.item_count, total_price: cart.total_price }
    }));
  }

  var sweeping = false;

  function sweep() {
    if (sweeping || !readFlag()) return;
    sweeping = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart-read-failed'); return r.json(); })
      .then(function (cart) {
        var vendor = hiddenVendor();
        var items = cart.items || [];
        var magic = items.filter(function (it) { return it.vendor === vendor; });

        if (!magic.length) {
          /* Checkout completed (Shopify emptied the cart) or nothing ever
             landed - either way the stamp's job is done. */
          clearFlag();
          sweeping = false;
          return;
        }

        if (magic.length === items.length) {
          /* Nothing but magic lines - the normal case straight after an
             abandoned hand-off. clear.js also resets the hand-off's
             attributes and note in the same call. */
          return fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } })
            .then(function (r) { if (!r.ok) throw new Error('clear-failed'); return r.json(); })
            .then(function (cleared) {
              clearFlag();
              sweeping = false;
              announce(cleared);
            });
        }

        /* Retail lines are present too - surgical removal by line KEY, and
           the hand-off's attributes/note are blanked so they cannot ride on
           the retail order. */
        var updates = {};
        magic.forEach(function (it) { updates[String(it.key)] = 0; });
        var attrs = {};
        HANDOFF_ATTRS.forEach(function (k) { attrs[k] = ''; });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates, attributes: attrs, note: '' })
        })
          .then(function (r) { if (!r.ok) throw new Error('update-failed'); return r.json(); })
          .then(function (updated) {
            clearFlag();
            sweeping = false;
            announce(updated);
          });
      })
      .catch(function () {
        /* Network hiccup: keep the stamp so the next page view retries. */
        sweeping = false;
      });
  }

  /* Three ways back from a checkout, and a fresh load is only one of them:
     bfcache restore fires pageshow (persisted) with no load event, and a
     tab left open behind the checkout tab fires visibilitychange when the
     shopper returns to it. All three are stamp-gated, so they are free in
     the steady state. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) sweep(); });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) sweep();
  });
})();
